import { useState } from 'react';
import { Button, Chip, IconButton, Stack, TextField, MenuItem, Tooltip, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import { Add, Payments, Delete, Link as LinkIcon, UploadFile, CloudDownload, AutoAwesome } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { FileUploadField } from '../../components/FileUploadField';
import { DataTable, type Column } from '../../components/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { AmFilterBar, type AmFilters } from '../../components/AmFilterBar';
import { BulkImportDialog } from '../../components/BulkImportDialog';
import { RequestPaymentDialog } from '../../components/RequestPaymentDialog';
import { amBulkApi } from '../../api/amBulk.api';
import { amElectricityApi, type AmElectricity, type CreateElectricityInput, type ExtractedBill } from '../../api/amElectricity.api';
import { amStationsApi } from '../../api/amStations.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
const firstOfMonth = () => new Date().toISOString().slice(0, 8) + '01';
const statusColor = (s: string): 'success' | 'warning' | 'error' | 'default' => (s === 'paid' ? 'success' : s === 'overdue' ? 'error' : 'warning');

export function ElectricityPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [filters, setFilters] = useState<AmFilters>({});
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<CreateElectricityInput>({ stationId: 0, monthYear: firstOfMonth(), billAmount: 0, dueDate: firstOfMonth() });
  const [requestRow, setRequestRow] = useState<AmElectricity | null>(null);
  const [deleting, setDeleting] = useState<AmElectricity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [extractMsg, setExtractMsg] = useState<{ severity: 'info' | 'success' | 'warning'; text: string } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [fetchOpen, setFetchOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['am-electricity', page, limit, filters],
    queryFn: () => amElectricityApi.list({ page, limit, status: filters.status || undefined, discomName: filters.discom || undefined, clientId: filters.clientId || undefined, city: filters.city || undefined, from: filters.from || undefined, to: filters.to || undefined }),
  });
  const { data: stations } = useQuery({ queryKey: ['am-stations', 'all'], queryFn: () => amStationsApi.list({ limit: 500, status: 'active' }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['am-electricity'] });

  const createMut = useMutation({
    mutationFn: () => amElectricityApi.create(form),
    onSuccess: () => { setFormOpen(false); setForm({ stationId: 0, monthYear: firstOfMonth(), billAmount: 0, dueDate: firstOfMonth() }); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => amElectricityApi.remove(id),
    onSuccess: () => { setDeleting(null); invalidate(); },
    onError: (e) => { setToast(extractApiError(e)); setDeleting(null); },
  });

  // Merge extracted fields into the form without wiping anything the user already typed.
  const applyExtracted = (b: ExtractedBill) => setForm((f) => ({
    ...f,
    consumerNumber: b.consumerNumber ?? f.consumerNumber,
    discomName: b.discomName ?? f.discomName,
    monthYear: b.billMonth ? `${b.billMonth}-01` : f.monthYear,
    unitsConsumed: b.unitsConsumed ?? f.unitsConsumed,
    billAmount: b.billAmount ?? f.billAmount,
    dueDate: b.dueDate ?? f.dueDate,
    consumerName: b.consumerName ?? f.consumerName,
    billNumber: b.billNumber ?? f.billNumber,
    billDate: b.billDate ?? f.billDate,
  }));

  const extractMut = useMutation({
    mutationFn: (url: string) => amElectricityApi.extract(url),
    onSuccess: (b) => {
      if (!b.configured) { setExtractMsg({ severity: 'warning', text: b.message ?? 'Document AI is not configured — enter the bill manually.' }); return; }
      applyExtracted(b);
      const got = [
        b.billAmount != null && 'amount', b.dueDate && 'due date', b.unitsConsumed != null && 'units',
        b.consumerNumber && 'consumer no.', b.discomName && 'DISCOM', b.billMonth && 'month',
      ].filter(Boolean) as string[];
      setExtractMsg(got.length
        ? { severity: 'success', text: `Auto-filled from bill: ${got.join(', ')}. Please verify the fields below before saving.` }
        : { severity: 'warning', text: b.message ?? 'Could not read the bill — enter the details manually.' });
    },
    onError: (e) => setExtractMsg({ severity: 'warning', text: extractApiError(e) }),
  });

  const columns: Column<AmElectricity>[] = [
    { key: 'stationName', label: 'Site', render: (r) => <span>{r.stationName ?? '—'}<br /><small style={{ color: '#888' }}>{r.city ?? ''}</small></span> },
    { key: 'discomName', label: 'DISCOM', render: (r) => <span>{r.discomName ?? '—'}<br /><small style={{ color: '#888' }}>{r.consumerNumber ?? ''}</small></span> },
    { key: 'monthYear', label: 'Month', render: (r) => (r.monthYear ? new Date(r.monthYear).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '—') },
    { key: 'unitsConsumed', label: 'Units', align: 'right', render: (r) => (r.unitsConsumed ?? '—') },
    { key: 'billAmount', label: 'Bill', align: 'right', render: (r) => inr(r.billAmount) },
    { key: 'dueDate', label: 'Due', render: (r) => (r.dueDate ? new Date(r.dueDate).toLocaleDateString('en-IN') : '—') },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={statusColor(r.status)} /> },
  ];

  return (
    <>
      <PageHeader title="Electricity Bill Management" subtitle="Monthly DISCOM bills paid on behalf of clients."
        action={hasPermission('am_electricity.create') && (
          <Stack direction="row" spacing={1}>
            <Button startIcon={<CloudDownload />} variant="outlined" onClick={() => setFetchOpen(true)}>Fetch Bill</Button>
            <Button startIcon={<UploadFile />} variant="outlined" onClick={() => setBulkOpen(true)}>Bulk Upload</Button>
            <Button startIcon={<Add />} variant="contained" onClick={() => { setError(null); setExtractMsg(null); setFormOpen(true); }}>New Bill</Button>
          </Stack>
        )} />

      <AmFilterBar value={filters} onChange={(f) => { setFilters(f); setPage(1); }} show={['status', 'client', 'discom', 'city', 'dates']} statusOptions={['pending', 'paid', 'overdue']} />

      <DataTable
        columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id}
        actions={(r) => (
          <>
            {r.status !== 'paid' && hasPermission('am_payment.raise') && (
              <Tooltip title="Request Payment (approval)"><IconButton size="small" color="primary" onClick={() => setRequestRow(r)}><Payments fontSize="small" /></IconButton></Tooltip>
            )}
            {r.billDocumentUrl && <Tooltip title="Bill document"><IconButton size="small" onClick={() => window.open(r.billDocumentUrl!, '_blank')}><LinkIcon fontSize="small" /></IconButton></Tooltip>}
            {hasPermission('am_electricity.update') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />

      {/* New bill */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Electricity Bill</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            {/* Upload the bill PDF/image → auto-extract the fields below with AI. */}
            <Alert severity="info" icon={<AutoAwesome fontSize="inherit" />} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
              <Typography variant="body2" sx={{ mb: 1 }}>Upload the electricity bill (PDF or image) to auto-fill the details below.</Typography>
              <FileUploadField
                label="Bill Document (PDF / image)"
                value={form.billDocumentUrl}
                onChange={(url) => {
                  setForm((f) => ({ ...f, billDocumentUrl: url ?? undefined }));
                  if (url) { setExtractMsg({ severity: 'info', text: 'Reading the bill…' }); extractMut.mutate(url); }
                  else setExtractMsg(null);
                }}
              />
              {form.billDocumentUrl && (
                <Button size="small" startIcon={extractMut.isPending ? <CircularProgress size={14} /> : <AutoAwesome />} disabled={extractMut.isPending}
                  sx={{ mt: 1 }} onClick={() => { setExtractMsg({ severity: 'info', text: 'Reading the bill…' }); extractMut.mutate(form.billDocumentUrl!); }}>
                  {extractMut.isPending ? 'Extracting…' : 'Re-extract from bill'}
                </Button>
              )}
            </Alert>
            {extractMsg && <Alert severity={extractMsg.severity}>{extractMsg.text}</Alert>}

            <TextField select label="Site *" size="small" value={form.stationId || ''} onChange={(e) => setForm((f) => ({ ...f, stationId: Number(e.target.value) }))}>
              {(stations?.data ?? []).map((s) => <MenuItem key={s.id} value={s.id}>{s.stationName} — {s.city ?? ''}</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField label="Consumer No." size="small" fullWidth value={form.consumerNumber ?? ''} onChange={(e) => setForm((f) => ({ ...f, consumerNumber: e.target.value }))} />
              <TextField label="DISCOM" size="small" fullWidth value={form.discomName ?? ''} onChange={(e) => setForm((f) => ({ ...f, discomName: e.target.value }))} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Month" type="month" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.monthYear.slice(0, 7)} onChange={(e) => setForm((f) => ({ ...f, monthYear: `${e.target.value}-01` }))} />
              <TextField label="Units" type="number" size="small" fullWidth value={form.unitsConsumed ?? ''} onChange={(e) => setForm((f) => ({ ...f, unitsConsumed: Number(e.target.value) }))} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Bill Amount (₹) *" type="number" size="small" fullWidth value={form.billAmount || ''} onChange={(e) => setForm((f) => ({ ...f, billAmount: Number(e.target.value) }))} />
              <TextField label="Due Date *" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.dueDate} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} />
            </Stack>
            <Typography variant="caption" color="text.secondary">A reminder is auto-scheduled 3 days before the due date.</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!form.stationId || !form.billAmount || createMut.isPending} onClick={() => { setError(null); createMut.mutate(); }}>Create</Button>
        </DialogActions>
      </Dialog>

      <RequestPaymentDialog
        open={Boolean(requestRow)} onClose={() => setRequestRow(null)}
        entityType="electricity" entityId={requestRow?.id ?? null} title={`Request electricity payment — ${requestRow?.stationName ?? ''}`}
        defaultAmount={requestRow ? requestRow.billAmount + (requestRow.lateFee ?? 0) : undefined}
        onDone={() => { setToast('Payment requested — sent for approval.'); invalidate(); }}
      />

      <BulkImportDialog
        open={bulkOpen} onClose={() => setBulkOpen(false)} title="Bulk upload electricity bills" entityLabel="Bill" showCreateLogins={false}
        columns={[
          { header: 'Site', sample: 'AMS000001', required: true },
          { header: 'Consumer Number', sample: 'CN-99' },
          { header: 'DISCOM', sample: 'BSES' },
          { header: 'Month', sample: '2026-06', required: true },
          { header: 'Units', sample: '1200' },
          { header: 'Bill Amount', sample: '8740', required: true },
          { header: 'Due Date', sample: '2026-06-12', required: true },
        ]}
        onImport={(rows) => amBulkApi.electricity(rows)} onDone={invalidate}
      />
      <ConfirmDialog open={Boolean(deleting)} title="Delete bill" message={`Delete the ${deleting?.monthYear ? new Date(deleting.monthYear).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : ''} electricity bill?`} confirmLabel="Delete"
        onCancel={() => setDeleting(null)} onConfirm={() => deleting && deleteMut.mutate(deleting.id)} />
      {fetchOpen && <FetchBillDialog stations={stations?.data ?? []} onClose={() => setFetchOpen(false)} onDone={(msg) => { setToast(msg); invalidate(); }} />}
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}

function FetchBillDialog({ stations, onClose, onDone }: { stations: { id: number; stationName: string; city: string | null }[]; onClose: () => void; onDone: (msg: string) => void }) {
  const [form, setForm] = useState({ stationId: '' as number | '', consumerNumber: '', discomName: '' });
  const [result, setResult] = useState<import('../../api/amElectricity.api').FetchBillResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inr = (n?: number | null) => (n == null ? '—' : `₹${Number(n).toLocaleString('en-IN')}`);

  const mut = useMutation({
    mutationFn: () => amElectricityApi.fetch({ stationId: form.stationId ? Number(form.stationId) : undefined, consumerNumber: form.consumerNumber || undefined, discomName: form.discomName || undefined }),
    onSuccess: (r) => {
      setResult(r); setError(null);
      if (r.configured && r.bill) onDone(r.saved ? `Bill fetched & added (${r.bill.billMonth})` : r.alreadyExists ? `Bill already recorded (${r.bill.billMonth})` : 'Bill fetched');
    },
    onError: (e) => setError(extractApiError(e)),
  });
  const canFetch = (Boolean(form.stationId) || form.consumerNumber.trim().length > 0) && !mut.isPending;

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>Fetch Electricity Bill</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField select label="Site (to save the bill against)" value={form.stationId} onChange={(e) => setForm({ ...form, stationId: e.target.value ? Number(e.target.value) : '' })} fullWidth
            helperText="Consumer number is auto-picked from the site if saved; leave blank & enter it below to just preview.">
            <MenuItem value="">— No site (preview only) —</MenuItem>
            {stations.map((s) => <MenuItem key={s.id} value={s.id}>{s.stationName}{s.city ? ` — ${s.city}` : ''}</MenuItem>)}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField label="Consumer Number" value={form.consumerNumber} onChange={(e) => setForm({ ...form, consumerNumber: e.target.value })} fullWidth helperText="Override / required if no site" />
            <TextField label="DISCOM" value={form.discomName} onChange={(e) => setForm({ ...form, discomName: e.target.value })} fullWidth />
          </Stack>

          {result && !result.configured && <Alert severity="warning">{result.message}</Alert>}
          {result?.bill && (
            <Alert severity={result.alreadyExists ? 'info' : 'success'}>
              <Typography variant="subtitle2">{result.bill.discom ?? '—'} · {result.bill.billMonth}</Typography>
              <Typography variant="body2">Amount <strong>{inr(result.bill.amount)}</strong> · Units {result.bill.units ?? '—'} · Due {result.bill.dueDate ?? '—'}</Typography>
              <Typography variant="caption" color="text.secondary">
                Consumer {result.bill.consumerNumber}{result.bill.consumerName ? ` (${result.bill.consumerName})` : ''} · Bill No {result.bill.billNumber ?? '—'} · via {result.bill.provider}
                {result.bill.billPdfUrl ? <> · <a href={result.bill.billPdfUrl} target="_blank" rel="noreferrer">Download bill</a></> : ' · (PDF available with a real provider)'}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{result.saved ? '✓ Added as a pending bill.' : result.alreadyExists ? 'Already recorded for this month.' : 'Preview only (pick a site to save).'}</Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" disabled={!canFetch} onClick={() => mut.mutate()}>Fetch Bill</Button>
      </DialogActions>
    </Dialog>
  );
}
